/**
 * AIAssistant + formatAIMessage.
 *
 * Moved from App.js in PR C-2 Move 4b of Option C. Pure mechanical
 * extraction; zero behavior changes. formatAIMessage is co-located
 * because it is only used by AIAssistant.
 */

import React, { useState, useEffect, useRef } from "react";
import { authApi } from "../../api";
import { tokensV2 as T } from "../../preview/v2/tokens.v2";

/* ═══════════════════════════════════════════════════════════
   AI ASSISTANT
═══════════════════════════════════════════════════════════ */

export function AIAssistant({ token }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "ai", text: "Hi! I'm your Fancall data assistant. Ask me anything about revenue, users, calls, creators, or campaigns.\n\nTry: *\"What was the total revenue in June 2025?\"*" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const ask = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: q }]);
    setLoading(true);
    try {
      const result = await authApi.askAI(token, q);
      setMessages(prev => [...prev, {
        role: "ai",
        text: result.answer,
        query: result.query,
        rowCount: result.rowCount,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: "ai", text: `Sorry, I encountered an error: ${err.message}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const [showSql, setShowSql] = useState(null);

  const clearChat = () => {
    setMessages([{ role: "ai", text: "Hi! I'm your Fancall data assistant. Ask me anything about revenue, users, calls, creators, or campaigns.\n\nTry: *\"What was the total revenue in June 2025?\"*" }]);
    setShowSql(null);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="fc-ai-fab"
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9999,
          width: 58, height: 58, borderRadius: "50%", border: "none", cursor: "pointer",
          background: `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
          boxShadow: `0 8px 24px ${T.brand.soft}, 0 0 0 0 ${T.brand.soft}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.2s", color: "#FFF",
          animation: "fc-ai-pulse 2.5s ease-in-out infinite",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
        title="Ask AI — your data assistant">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <style>{`
          @keyframes fc-ai-pulse {
            0%, 100% { box-shadow: 0 8px 24px rgba(252,128,25,0.35), 0 0 0 0 rgba(252,128,25,0.45); }
            50%      { box-shadow: 0 8px 24px rgba(252,128,25,0.35), 0 0 0 14px rgba(252,128,25,0); }
          }
          @media (max-width: 768px) {
            .fc-ai-fab { bottom: 16px !important; right: 16px !important; width: 52px !important; height: 52px !important; }
          }
        `}</style>
      </button>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fc-ai-dot { 0%,80%,100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1); } }
        .fc-ai-dot { animation: fc-ai-dot 1.2s ease-in-out infinite; }
        /* The wrapper around the input already shows the orange focus ring.
           Suppress the global :focus-visible box-shadow on the input itself
           so we don't get a double-ring (one on the wrapper, one on the input). */
        .fc-ai-input:focus-visible { box-shadow: none !important; outline: none !important; }
        @media (max-width: 768px) {
          .fc-ai-panel { inset: 0 !important; width: 100% !important; height: 100% !important;
            bottom: auto !important; right: auto !important; border-radius: 0 !important;
            border: none !important; }
        }
      `}</style>
      <div className="fc-ai-panel"
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9999,
          width: 440, height: 620, borderRadius: 18, overflow: "hidden",
          display: "flex", flexDirection: "column",
          background: T.surface.canvas,
          border: `1px solid ${T.border.subtle}`,
          boxShadow: "0 24px 64px rgba(31,42,63,0.18)",
          fontFamily: T.font.sans,
        }}>
      {/* Header */}
      <div style={{
        padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: T.surface.card,
        borderBottom: `1px solid ${T.border.subtle}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 12px ${T.brand.soft}`,
          }}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.text.primary, letterSpacing: "-0.01em" }}>Ask AI</div>
            <div style={{ fontSize: 10, color: T.text.muted, fontWeight: 600, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.status.good, boxShadow: `0 0 6px ${T.status.good}` }}/>
              Online · powered by Claude
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={clearChat} title="Clear chat"
            style={{ background: "transparent", border: `1px solid ${T.border.subtle}`, cursor: "pointer",
              width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
              color: T.text.muted, transition: "all 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.background = T.surface.canvas; e.currentTarget.style.color = T.text.primary; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.text.muted; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
            </svg>
          </button>
          <button onClick={() => setOpen(false)} title="Close"
            style={{ background: "transparent", border: `1px solid ${T.border.subtle}`, cursor: "pointer",
              width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
              color: T.text.muted, fontSize: 18, transition: "all 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.background = T.status.badBg; e.currentTarget.style.color = T.status.bad; e.currentTarget.style.borderColor = `${T.status.bad}55`; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.text.muted; e.currentTarget.style.borderColor = T.border.subtle; }}>
            ×
          </button>
        </div>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: "auto", padding: "18px 16px",
        display: "flex", flexDirection: "column", gap: 14,
        background: T.surface.canvas,
      }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: "flex", flexDirection: "column",
            alignItems: msg.role === "user" ? "flex-end" : "flex-start",
          }}>
            {msg.role === "ai" && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <div style={{ width: 18, height: 18, borderRadius: 5,
                  background: `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: T.text.muted, letterSpacing: "0.04em" }}>AI</span>
              </div>
            )}
            <div style={{
              maxWidth: "85%", padding: "11px 15px", borderRadius: 14,
              fontSize: 13, lineHeight: 1.6, wordBreak: "break-word",
              ...(msg.role === "user" ? {
                background: `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
                color: "#FFFFFF",
                borderBottomRightRadius: 4,
                fontWeight: 500,
                boxShadow: `0 4px 12px ${T.brand.soft}`,
              } : {
                background: T.surface.card,
                border: `1px solid ${T.border.subtle}`,
                color: msg.error ? T.status.bad : T.text.primary,
                borderBottomLeftRadius: 4,
              }),
            }}
            dangerouslySetInnerHTML={{ __html: formatAIMessage(msg.text) }}
            />
            {msg.query && (
              <button onClick={() => setShowSql(showSql === i ? null : i)}
                style={{
                  fontSize: 10, color: showSql === i ? T.brand.base : T.text.muted,
                  background: showSql === i ? T.brand.soft : "transparent",
                  border: `1px solid ${showSql === i ? T.brand.base + "55" : "transparent"}`,
                  cursor: "pointer", marginTop: 6, padding: "4px 10px", borderRadius: 6,
                  fontWeight: 700, transition: "all 0.15s", fontFamily: "inherit",
                }}>
                {showSql === i ? "◂ Hide SQL" : "▸ View SQL"}{msg.rowCount != null ? ` · ${msg.rowCount} rows` : ""}
              </button>
            )}
            {showSql === i && msg.query && (
              <pre className="fc-mono" style={{
                fontSize: 10.5, color: T.text.primary, background: T.surface.card,
                padding: "10px 12px", borderRadius: 8, marginTop: 6, maxWidth: "85%",
                overflow: "auto", border: `1px solid ${T.border.subtle}`, whiteSpace: "pre-wrap",
                wordBreak: "break-word", lineHeight: 1.5, margin: "6px 0 0",
              }}>{msg.query}</pre>
            )}
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <div style={{ width: 18, height: 18, borderRadius: 5,
                background: `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.text.muted, letterSpacing: "0.04em" }}>AI</span>
            </div>
            <div style={{
              padding: "12px 16px", borderRadius: 14, borderBottomLeftRadius: 4,
              background: T.surface.card, border: `1px solid ${T.border.subtle}`,
            }}>
              <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                <span className="fc-ai-dot" style={{ width: 8, height: 8, borderRadius: "50%", background: T.brand.base, animationDelay: "0s" }}/>
                <span className="fc-ai-dot" style={{ width: 8, height: 8, borderRadius: "50%", background: T.brand.base, animationDelay: "0.15s" }}/>
                <span className="fc-ai-dot" style={{ width: 8, height: 8, borderRadius: "50%", background: T.brand.base, animationDelay: "0.3s" }}/>
                <span style={{ fontSize: 11, color: T.text.secondary, marginLeft: 8, fontWeight: 500 }}>Analysing your data…</span>
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef}/>
      </div>

      {/* Suggestion chips — shown on empty chat */}
      {messages.length <= 1 && !loading && (
        <div style={{ padding: "4px 16px 14px" }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.12em", color: T.text.muted,
            textTransform: "uppercase", marginBottom: 8 }}>
            Suggestions
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              "Total revenue this month vs last month?",
              "How many new users signed up this week?",
              "Top 5 creators by earnings",
              "Monthly revenue trend for 2025",
              "How many completed calls in May 2025?",
            ].map((s, i) => (
              <button key={i} onClick={() => { setInput(s); inputRef.current?.focus(); }}
                style={{
                  fontSize: 12, padding: "10px 14px", borderRadius: 10, cursor: "pointer",
                  background: T.surface.card, border: `1px solid ${T.border.subtle}`, color: T.text.primary,
                  transition: "all 0.15s", textAlign: "left", fontFamily: "inherit",
                  display: "flex", alignItems: "center", gap: 10, fontWeight: 500,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.brand.base; e.currentTarget.style.background = T.surface.cardHover; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border.subtle; e.currentTarget.style.background = T.surface.card; }}>
                <span style={{ color: T.brand.base, fontSize: 14, fontWeight: 700 }}>›</span>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div style={{
        padding: "14px 16px", borderTop: `1px solid ${T.border.subtle}`,
        background: T.surface.card,
      }}>
        <div style={{
          display: "flex", gap: 8, alignItems: "center",
          background: T.surface.canvas, borderRadius: 12,
          border: `1px solid ${T.border.default}`,
          padding: "5px 5px 5px 16px",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
        onFocusCapture={(e) => { e.currentTarget.style.borderColor = T.brand.base; e.currentTarget.style.boxShadow = `0 0 0 3px ${T.brand.soft}`; }}
        onBlurCapture={(e) => { e.currentTarget.style.borderColor = T.border.default; e.currentTarget.style.boxShadow = "none"; }}>
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
            placeholder="Ask about revenue, users, calls…"
            disabled={loading}
            className="fc-ai-input"
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: T.text.primary, fontSize: 13, fontFamily: "inherit", padding: "6px 0",
              fontWeight: 500,
            }}
          />
          <button onClick={ask} disabled={loading || !input.trim()}
            aria-label="Send message"
            style={{
              width: 36, height: 36, borderRadius: 9, border: "none",
              cursor: loading || !input.trim() ? "not-allowed" : "pointer",
              background: input.trim() && !loading
                ? `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`
                : T.border.subtle,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.15s", flexShrink: 0,
              boxShadow: input.trim() && !loading ? `0 4px 10px ${T.brand.soft}` : "none",
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke={input.trim() && !loading ? "#FFF" : T.text.muted}
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
        <div style={{ fontSize: 9.5, color: T.text.muted, marginTop: 8, textAlign: "center",
          fontWeight: 500, letterSpacing: "0.02em" }}>
          AI can make mistakes — verify important numbers against source data
        </div>
      </div>
      </div>
    </>
  );
}

/** Format AI markdown-ish text to simple HTML */
// SECURITY: HTML-escape FIRST to neutralize any injected tags,
// then apply safe markdown→HTML transforms. Order matters.
function formatAIMessage(text) {
  if (!text) return "";
  // Step 1: Escape all HTML entities (prevents XSS)
  let safe = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  // Step 2: Apply markdown formatting (only creates safe, known tags)
  return safe
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, '<code style="background:rgba(51,65,85,0.5);padding:1px 4px;border-radius:3px;font-size:11px">$1</code>')
    .replace(/^### (.+)$/gm, '<div style="font-size:13px;font-weight:800;margin:8px 0 4px;color:#E2E8F0">$1</div>')
    .replace(/^## (.+)$/gm, '<div style="font-size:14px;font-weight:800;margin:8px 0 4px;color:#FFF">$1</div>')
    .replace(/^- (.+)$/gm, '<div style="padding-left:12px">• $1</div>')
    .replace(/^(\d+)\. (.+)$/gm, '<div style="padding-left:12px">$1. $2</div>')
    .replace(/\n/g, "<br/>");
}

